import { useState } from 'react';

import { useRouter } from 'next/router';
import { useRentalManagerUserPropertyGetFieldsWebApiHandler_getPropertyFields } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import {
  Accordion,
  Box,
  FormField,
  FormHelp,
  Heading,
  Icon,
  Label,
  Paragraph,
  Tag,
  Text,
  Textarea,
  TextButton,
} from '@zillow/constellation-10';
import { IconAIMagicFilled } from '@zillow/constellation-icons';

import AIListingCreationIntroductionModal from '../../../modals/AIListingCreationIntroductionModal';
import AIListingNextConfirmModal from './components/AIListingNextConfirmModal';

import {
  wizardFlexContainerStyles,
  wizardFormSectionContainerStyles,
  wizardParagraphStyles,
} from '../../../../styles/wizard';

import PhotoUploadFormField from '../../../PostingPath/MediaContent/PhotoUploadFormField';
import PageWizard from '../../../Wizard/PageWizard';

import type { ChangeEvent } from 'react';
import type { WizardBodyTemplateProps } from '../../../WizardStepController';

type Props = WizardBodyTemplateProps & {
  onBuildManually?: () => void;
};

const AIListingPhotosAndNotes = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  onBuildManually,
  isNextDisabled,
  ...rest
}: Props) => {
  const router = useRouter();
  const listingAlias = String(router.query.listingAlias || '');
  const [propertyNotes, setPropertyNotes] = useState('');
  const [showIntroModal, setShowIntroModal] = useState(true);
  const closeIntroModal = () => setShowIntroModal(false);
  const [showNextConfirmModal, setShowNextConfirmModal] = useState(false);

  const { mutate: getPropertyFields } = useRentalManagerUserPropertyGetFieldsWebApiHandler_getPropertyFields();

  return (
    <PageWizard
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onGoToNextStep ? () => setShowNextConfirmModal(true) : undefined}
      isNextDisabled={Boolean(isNextDisabled) || !propertyNotes}
      onBackClick={onGoToPrevStep || undefined}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      {...rest}
    >
      <AIListingCreationIntroductionModal
        open={showIntroModal}
        onClose={closeIntroModal}
        onBuildManually={closeIntroModal}
        onDraftWithAI={closeIntroModal}
      />
      <AIListingNextConfirmModal
        open={showNextConfirmModal}
        onClose={() => setShowNextConfirmModal(false)}
        onContinue={() => {
          getPropertyFields({ getParams: { listingAlias }, postData: { text: propertyNotes } });
          setShowNextConfirmModal(false);
          onGoToNextStep?.();
        }}
      />
      <div data-testid="AIListingPhotosAndNotes">
        <Box css={wizardFlexContainerStyles}>
          <Box css={wizardFormSectionContainerStyles.raw()}>
            <Box css={{ display: 'flex', alignItems: 'center', gap: '400', marginBottom: '400' }}>
              <Heading level={2} textStyle="heading-md">
                Let’s build your listing
              </Heading>
              <Tag tone="purple" icon={<Icon render={<IconAIMagicFilled />} size="sm" />}>
                AI assisted
              </Tag>
            </Box>
            <Paragraph css={wizardParagraphStyles}>
              AI can help build your listing faster by processing your photos and notes to draft a property description
              and suggest amenities. You’ll be asked for details AI can’t determine, like rent and screening criteria.
            </Paragraph>
            <Text textStyle="body-sm-bold" css={{ marginBottom: '100', display: 'block' }}>
              Property photos
            </Text>
            <PhotoUploadFormField />
            <Box css={{ marginTop: '800' }}>
              <FormField
                label={<Label>Property notes</Label>}
                required
                description={
                  <FormHelp>
                    Add details that complement your photos or describe what the photos don&apos;t show, including what
                    makes your property special. Note: Content that may violate Zillow&apos;s Respectful Renting Pledge
                    will not be included in your description, or will stop the description from being generated.
                  </FormHelp>
                }
                control={
                  <Textarea
                    value={propertyNotes}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setPropertyNotes(e.target.value)}
                    placeholder="Example: Basement is storage only. Shared driveway with next-door neighbor. No musical instruments."
                    rows={6}
                  />
                }
              />
            </Box>

            <Box css={{ marginTop: '800' }}>
              <Accordion.Root>
                <Accordion.Item value="tips">
                  <Accordion.Header>
                    <Heading level={3} textStyle="heading-xs">
                      Tips to get best results
                    </Heading>
                  </Accordion.Header>
                  <Accordion.Panel>
                    <Box css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
                      <Box>
                        <Text textStyle="body-sm-bold" css={{ display: 'block', marginBottom: '200' }}>
                          A picture says 1,000 words
                        </Text>
                        <Paragraph>
                          Since we use photos to help draft your property description, include images that feature what
                          makes your place unique.
                        </Paragraph>
                      </Box>
                      <Box>
                        <Text textStyle="body-sm-bold" css={{ display: 'block', marginBottom: '200' }}>
                          Consider what matters to renters
                        </Text>
                        <Paragraph>
                          Use your notes to reference high priority details like location and transit options.
                        </Paragraph>
                      </Box>
                      <Box>
                        <Text textStyle="body-sm-bold" css={{ display: 'block', marginBottom: '200' }}>
                          Make this home stand out
                        </Text>
                        <Paragraph>
                          In both photos and notes, include any upgrades, attractive amenities, and unique details.
                        </Paragraph>
                      </Box>
                    </Box>
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion.Root>
            </Box>
            <TextButton tone="brand" onClick={onBuildManually} css={{ marginTop: '600' }}>
              Build listing manually instead
            </TextButton>
          </Box>
        </Box>
      </div>
    </PageWizard>
  );
};

export default AIListingPhotosAndNotes;
